import type {
  BrandAssetHash as BrandAssetHashType,
  ResolvedBrandFont,
} from "@ascleo/frontdesk-contracts/branding/model";
import {
  BrandingVersionWaitResult,
  PublishedBrandingManifest,
  type PublishedBrandingContent,
  type PublishedBrandingManifest as PublishedBrandingManifestType,
} from "@ascleo/frontdesk-contracts/branding/public";
import { Result as Either, Schema } from "effect";
import { practiceStyleFromContent, safeBrandFontFamily } from "./branding-css";
import { DEFAULT_ACTIVE_BRANDING } from "./branding-default";
import {
  CachedBrandingAsset,
  CachedBrandingRecord,
  type ActiveBrandingSnapshot,
  type BrandingResourceLoader,
  type BrandingRuntime,
  type BrandingRuntimeDependencies,
  type BrandingRuntimeSnapshot,
  type LoadedBrandFont,
} from "./branding-types";

type StageCategory = "manifest" | "asset" | "image" | "font" | "cache";

class BrandingStageError extends Error {
  readonly category: StageCategory;

  constructor(category: StageCategory, message: string) {
    super(message);
    this.name = "BrandingStageError";
    this.category = category;
  }
}

interface AssetReference {
  readonly assetHash: BrandAssetHashType;
  readonly mediaType:
    | "image/png"
    | "image/webp"
    | "image/svg+xml"
    | "font/woff2"
    | "font/ttf"
    | "font/otf";
  readonly expectedByteLength?: number;
  readonly purpose: "image" | "font";
  readonly font?: ResolvedBrandFont;
}

interface RuntimeResources {
  readonly urls: Map<string, string>;
  readonly fonts: Map<string, LoadedBrandFont>;
}

interface PreparedBranding {
  readonly snapshot: ActiveBrandingSnapshot;
  readonly resources: RuntimeResources;
  readonly createdUrls: ReadonlyArray<string>;
  readonly createdFonts: ReadonlyArray<LoadedBrandFont>;
}

const decodeManifest = Schema.decodeUnknownResult(PublishedBrandingManifest, {
  onExcessProperty: "error",
});
const decodeWait = Schema.decodeUnknownResult(BrandingVersionWaitResult, {
  onExcessProperty: "error",
});
const decodeCachedRecord = Schema.decodeUnknownResult(CachedBrandingRecord, {
  onExcessProperty: "error",
});
const decodeCachedAsset = Schema.decodeUnknownResult(CachedBrandingAsset, {
  onExcessProperty: "error",
});

const silentObserver = { report: () => undefined };

function defaultSleep(
  milliseconds: number,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Branding wait aborted.", "AbortError"));
      return;
    }
    const timer = globalThis.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      "abort",
      () => {
        globalThis.clearTimeout(timer);
        reject(new DOMException("Branding wait aborted.", "AbortError"));
      },
      { once: true },
    );
  });
}

function defaultRetirement(retire: () => void): void {
  globalThis.setTimeout(retire, 0);
}

function defaultActive(): ActiveBrandingSnapshot {
  return DEFAULT_ACTIVE_BRANDING;
}

function referencesFromContent(
  content: PublishedBrandingContent,
): ReadonlyArray<AssetReference> {
  if (content.kind === "default") return [];
  const references: AssetReference[] = [
    {
      assetHash: content.primaryLogo.assetHash,
      mediaType: content.primaryLogo.mediaType,
      expectedByteLength: content.primaryLogo.byteLength,
      purpose: "image",
    },
  ];
  if (content.compactMark !== undefined) {
    references.push({
      assetHash: content.compactMark.assetHash,
      mediaType: content.compactMark.mediaType,
      expectedByteLength: content.compactMark.byteLength,
      purpose: "image",
    });
  }
  for (const font of [content.fonts.heading, content.fonts.body]) {
    if (font.source === "builtin") continue;
    for (const face of font.faces) {
      if (face.assetHash === undefined) continue;
      references.push({
        assetHash: face.assetHash,
        mediaType: face.mediaType ?? "font/woff2",
        purpose: "font",
        font,
      });
    }
  }
  const unique = new Map<string, AssetReference>();
  for (const reference of references) {
    const current = unique.get(reference.assetHash);
    if (
      current !== undefined &&
      (current.mediaType !== reference.mediaType ||
        current.purpose !== reference.purpose)
    ) {
      throw new BrandingStageError(
        "manifest",
        "Asset hash has conflicting roles.",
      );
    }
    unique.set(reference.assetHash, current ?? reference);
  }
  const ordered: AssetReference[] = [];
  for (const reference of unique.values()) {
    const insertionIndex = ordered.findIndex(
      (current) => reference.assetHash.localeCompare(current.assetHash) < 0,
    );
    if (insertionIndex === -1) {
      ordered.push(reference);
    } else {
      ordered.splice(insertionIndex, 0, reference);
    }
  }
  return ordered;
}

function hasRequiredWeights(font: ResolvedBrandFont): boolean {
  return [400, 500, 600, 700].every((weight) =>
    font.faces.some(
      (face) => face.weightMin <= weight && face.weightMax >= weight,
    ),
  );
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const digest = await crypto.subtle.digest("SHA-256", copy.buffer);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

async function validateAsset(
  raw: unknown,
  reference: AssetReference,
): Promise<import("./branding-types").CachedBrandingAsset> {
  const decoded = decodeCachedAsset(raw);
  if (Either.isFailure(decoded)) {
    throw new BrandingStageError("asset", "Asset envelope failed decode.");
  }
  const asset = decoded.success;
  if (
    asset.metadata.assetHash !== reference.assetHash ||
    asset.metadata.mediaType !== reference.mediaType ||
    asset.metadata.byteLength !== asset.bytes.byteLength ||
    (reference.expectedByteLength !== undefined &&
      reference.expectedByteLength !== asset.bytes.byteLength) ||
    (await sha256(asset.bytes)) !== reference.assetHash
  ) {
    throw new BrandingStageError(
      "asset",
      "Asset integrity verification failed.",
    );
  }
  return asset;
}

function cleanupPrepared(
  prepared: PreparedBranding,
  loader: BrandingResourceLoader,
): void {
  for (const font of prepared.createdFonts) loader.removeFont(font);
  for (const url of prepared.createdUrls) loader.revokeUrl(url);
}

async function prepareResources(
  manifest: PublishedBrandingManifestType,
  assets: ReadonlyMap<string, import("./branding-types").CachedBrandingAsset>,
  current: RuntimeResources,
  loader: BrandingResourceLoader,
  signal: AbortSignal,
  source: ActiveBrandingSnapshot["source"],
): Promise<PreparedBranding> {
  const references = referencesFromContent(manifest.content);
  const urls = new Map<string, string>();
  const fonts = new Map<string, LoadedBrandFont>();
  const createdUrls: string[] = [];
  const createdFonts: LoadedBrandFont[] = [];

  try {
    for (const reference of references) {
      const reusedUrl = current.urls.get(reference.assetHash);
      const url =
        reusedUrl ??
        loader.createUrl(
          assets.get(reference.assetHash) ??
            (() => {
              throw new BrandingStageError("asset", "Staged asset is missing.");
            })(),
        );
      urls.set(reference.assetHash, url);
      if (reusedUrl === undefined) createdUrls.push(url);

      if (reference.purpose === "image" && reusedUrl === undefined) {
        try {
          await loader.loadImage(url, signal);
        } catch (error) {
          if (signal.aborted) throw error;
          throw new BrandingStageError(
            "image",
            "Branding image failed decode.",
          );
        }
      }
      if (reference.purpose === "font") {
        const font = reference.font;
        if (font === undefined || !hasRequiredWeights(font)) {
          throw new BrandingStageError(
            "font",
            "Branding font coverage is incomplete.",
          );
        }
        const reusedFont = current.fonts.get(reference.assetHash);
        if (reusedFont !== undefined) {
          fonts.set(reference.assetHash, reusedFont);
        } else {
          try {
            const loaded = await loader.loadFont(
              font,
              reference.assetHash,
              url,
              signal,
            );
            fonts.set(reference.assetHash, loaded);
            createdFonts.push(loaded);
          } catch (error) {
            if (signal.aborted) throw error;
            throw new BrandingStageError("font", "Branding font failed load.");
          }
        }
      }
    }

    const content = manifest.content;
    return {
      snapshot: Object.freeze({
        source,
        manifest,
        style: practiceStyleFromContent(content),
        primaryLogoUrl:
          content.kind === "custom"
            ? urls.get(content.primaryLogo.assetHash)
            : undefined,
        compactMarkUrl:
          content.kind === "custom" && content.compactMark !== undefined
            ? urls.get(content.compactMark.assetHash)
            : undefined,
        visualDisplayName:
          content.kind === "custom" ? content.displayName : undefined,
        headingFontFamily:
          content.kind === "custom"
            ? safeBrandFontFamily(content.fonts.heading)
            : undefined,
        bodyFontFamily:
          content.kind === "custom"
            ? safeBrandFontFamily(content.fonts.body)
            : undefined,
      }),
      resources: { urls, fonts },
      createdUrls,
      createdFonts,
    };
  } catch (error) {
    for (const font of createdFonts) loader.removeFont(font);
    for (const url of createdUrls) loader.revokeUrl(url);
    throw error;
  }
}

/**
 * Construct the shared monotonic, stage-before-commit branding runtime.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { makeBrandingRuntime } from "@ascleo/frontdesk-shared/branding";
 *
 * const runtime = makeBrandingRuntime({ transport, cache, resources });
 * await runtime.bootstrap();
 * ```
 */
export function makeBrandingRuntime(
  dependencies: BrandingRuntimeDependencies,
): BrandingRuntime {
  const synchronizationPolicy =
    dependencies.synchronizationPolicy ?? "continuous";
  const observer = dependencies.observer ?? silentObserver;
  const sleep = dependencies.sleep ?? defaultSleep;
  const random = dependencies.random ?? Math.random;
  const deferRetirement = dependencies.deferRetirement ?? defaultRetirement;
  let lifecycle = new AbortController();
  const listeners = new Set<() => void>();
  let resources: RuntimeResources = { urls: new Map(), fonts: new Map() };
  let snapshot: BrandingRuntimeSnapshot = Object.freeze({
    active: defaultActive(),
    syncHealth: "idle",
  });
  let bootstrapped = false;
  let started = false;
  let disposed = false;
  let synchronization: Promise<void> | undefined;

  const publishSnapshot = (
    active: ActiveBrandingSnapshot,
    syncHealth: BrandingRuntimeSnapshot["syncHealth"],
  ): void => {
    snapshot = Object.freeze({ active, syncHealth });
    for (const listener of listeners) {
      try {
        listener();
      } catch {
        // One consumer cannot invalidate a committed runtime snapshot for every shell consumer.
      }
    }
  };

  const setHealth = (health: BrandingRuntimeSnapshot["syncHealth"]): void => {
    if (snapshot.syncHealth !== health)
      publishSnapshot(snapshot.active, health);
  };

  const retireResources = (
    previous: RuntimeResources,
    next: RuntimeResources,
  ): void => {
    deferRetirement(() => {
      try {
        for (const [hash, font] of previous.fonts) {
          if (next.fonts.get(hash) !== font)
            dependencies.resources.removeFont(font);
        }
        for (const [hash, url] of previous.urls) {
          if (next.urls.get(hash) !== url)
            dependencies.resources.revokeUrl(url);
        }
      } catch {
        observer.report({ kind: "cleanup-failed" });
      }
    });
  };

  const activate = (prepared: PreparedBranding): void => {
    for (const font of prepared.createdFonts) {
      dependencies.resources.registerFont(font);
    }
    for (const font of prepared.createdFonts) {
      if (!dependencies.resources.verifyFont(font)) {
        throw new BrandingStageError(
          "font",
          "Loaded font weights are unavailable.",
        );
      }
    }
    const previous = resources;
    resources = prepared.resources;
    publishSnapshot(prepared.snapshot, "healthy");
    observer.report({
      kind: "activated",
      version: prepared.snapshot.manifest.publicationVersion,
    });
    retireResources(previous, prepared.resources);
  };

  const bootstrap = async (): Promise<BrandingRuntimeSnapshot> => {
    if (bootstrapped) return snapshot;
    bootstrapped = true;
    let prepared: PreparedBranding | undefined;
    try {
      const rawRecord = await dependencies.cache.readActive();
      if (rawRecord === undefined)
        throw new BrandingStageError("cache", "No cache.");
      const decoded = decodeCachedRecord(rawRecord);
      if (Either.isFailure(decoded)) {
        throw new BrandingStageError("cache", "Active cache failed decode.");
      }
      const record = decoded.success;
      if (record.contentFingerprint !== record.manifest.fingerprint) {
        throw new BrandingStageError(
          "cache",
          "Cached manifest failed verification.",
        );
      }
      const references = referencesFromContent(record.manifest.content);
      const expectedHashes = references.map((reference) => reference.assetHash);
      if (
        expectedHashes.length !== record.referencedHashes.length ||
        expectedHashes.some(
          (hash, index) => hash !== record.referencedHashes[index],
        )
      ) {
        throw new BrandingStageError(
          "cache",
          "Cached asset set is incomplete.",
        );
      }
      const assets = new Map<
        string,
        import("./branding-types").CachedBrandingAsset
      >();
      for (const reference of references) {
        const rawAsset = await dependencies.cache.readAsset(
          reference.assetHash,
        );
        assets.set(
          reference.assetHash,
          await validateAsset(rawAsset, reference),
        );
      }
      prepared = await prepareResources(
        record.manifest,
        assets,
        resources,
        dependencies.resources,
        lifecycle.signal,
        "cache",
      );
      activate(prepared);
      observer.report({
        kind: "cache-used",
        version: record.manifest.publicationVersion,
      });
    } catch {
      if (prepared !== undefined)
        cleanupPrepared(prepared, dependencies.resources);
      publishSnapshot(defaultActive(), "idle");
      observer.report({ kind: "default-used" });
    }
    return snapshot;
  };

  const synchronizeOnce = async (): Promise<void> => {
    if (!bootstrapped) await bootstrap();
    const signal = lifecycle.signal;
    if (signal.aborted) return;
    setHealth("synchronizing");
    let prepared: PreparedBranding | undefined;
    let version: number | undefined;
    try {
      const rawManifest = await dependencies.transport.fetchManifest(signal);
      const decoded = decodeManifest(rawManifest);
      if (Either.isFailure(decoded)) {
        throw new BrandingStageError(
          "manifest",
          "Remote manifest failed decode.",
        );
      }
      const manifest = decoded.success;
      version = manifest.publicationVersion;
      if (
        manifest.publicationVersion <=
        snapshot.active.manifest.publicationVersion
      ) {
        setHealth("healthy");
        return;
      }
      observer.report({
        kind: "update-seen",
        version: manifest.publicationVersion,
      });
      const references = referencesFromContent(manifest.content);
      const assets = new Map<
        string,
        import("./branding-types").CachedBrandingAsset
      >();
      for (const reference of references) {
        let asset: import("./branding-types").CachedBrandingAsset | undefined;
        const cached = await dependencies.cache.readAsset(reference.assetHash);
        if (cached !== undefined) {
          try {
            asset = await validateAsset(cached, reference);
          } catch {
            asset = undefined;
          }
        }
        if (asset === undefined) {
          const fetched = await dependencies.transport.fetchAsset(
            reference.assetHash,
            signal,
          );
          asset = await validateAsset(fetched, reference);
          await dependencies.cache.stageAsset(asset);
        }
        assets.set(reference.assetHash, asset);
      }
      const record: import("./branding-types").CachedBrandingRecord = {
        cacheFormatVersion: 1,
        contentFingerprint: manifest.fingerprint,
        referencedHashes: references.map((reference) => reference.assetHash),
        manifest,
      };
      await dependencies.cache.stageRecord(record);
      prepared = await prepareResources(
        manifest,
        assets,
        resources,
        dependencies.resources,
        signal,
        "remote",
      );
      for (const font of prepared.createdFonts) {
        dependencies.resources.registerFont(font);
      }
      for (const font of prepared.createdFonts) {
        if (!dependencies.resources.verifyFont(font)) {
          throw new BrandingStageError(
            "font",
            "Loaded font weights are unavailable.",
          );
        }
      }
      await dependencies.cache.commitActive(manifest.publicationVersion);
      const previous = resources;
      resources = prepared.resources;
      publishSnapshot(prepared.snapshot, "healthy");
      observer.report({
        kind: "activated",
        version: manifest.publicationVersion,
      });
      retireResources(previous, prepared.resources);
      try {
        await dependencies.cache.cleanup(
          new Set(references.map((reference) => reference.assetHash)),
        );
      } catch {
        observer.report({ kind: "cleanup-failed" });
      }
    } catch (error) {
      if (prepared !== undefined)
        cleanupPrepared(prepared, dependencies.resources);
      if (version !== undefined) {
        await dependencies.cache.abandon(version).catch(() => undefined);
      }
      if (!signal.aborted) {
        const category =
          error instanceof BrandingStageError ? error.category : "cache";
        observer.report({ kind: "stage-failed", category });
        setHealth("degraded");
      }
    }
  };

  const synchronize = (): Promise<void> => {
    if (synchronization !== undefined) return synchronization;
    synchronization = synchronizeOnce().finally(() => {
      synchronization = undefined;
    });
    return synchronization;
  };

  const waitLoop = async (controller: AbortController): Promise<void> => {
    let failures = 0;
    while (!controller.signal.aborted) {
      try {
        const raw = await dependencies.transport.waitForChange(
          snapshot.active.manifest.publicationVersion,
          controller.signal,
        );
        const decoded = decodeWait(raw);
        if (Either.isFailure(decoded) || decoded.success.outcome === "unavailable") {
          throw new Error("Branding wait unavailable.");
        }
        failures = 0;
        setHealth("healthy");
        if (
          decoded.success.outcome === "changed" &&
          decoded.success.manifest.publicationVersion >
            snapshot.active.manifest.publicationVersion
        ) {
          await synchronize();
        }
      } catch {
        if (controller.signal.aborted) return;
        failures += 1;
        setHealth("degraded");
        observer.report({ kind: "wait-unavailable" });
        const base = Math.min(30_000, 500 * 2 ** Math.min(failures - 1, 6));
        await sleep(
          Math.round(base * (0.8 + random() * 0.4)),
          controller.signal,
        ).catch(() => undefined);
      }
    }
  };

  const launch = (waitForPriorSynchronization: boolean): void => {
    if (synchronizationPolicy === "bootstrap-only" || started || disposed) {
      return;
    }
    if (lifecycle.signal.aborted) lifecycle = new AbortController();
    started = true;
    const controller = lifecycle;
    const prior = synchronization;
    void (async () => {
      if (waitForPriorSynchronization && prior !== undefined) await prior;
      if (controller.signal.aborted || controller !== lifecycle) return;
      await synchronize();
      if (controller.signal.aborted || controller !== lifecycle) return;
      await waitLoop(controller);
    })();
  };

  return {
    bootstrap,
    synchronize,
    start: () => launch(false),
    pause: () => {
      if (disposed || lifecycle.signal.aborted) return;
      lifecycle.abort();
      started = false;
      publishSnapshot(snapshot.active, "idle");
    },
    resume: () => launch(true),
    stop: () => {
      if (disposed) return;
      disposed = true;
      if (!lifecycle.signal.aborted) lifecycle.abort();
      started = false;
      for (const font of resources.fonts.values())
        dependencies.resources.removeFont(font);
      for (const url of resources.urls.values())
        dependencies.resources.revokeUrl(url);
      resources = { urls: new Map(), fonts: new Map() };
      publishSnapshot(snapshot.active, "stopped");
    },
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
